import { z } from 'zod'
import { isMonday } from '../lunch-menu'

export const MENU_SCAN_CREDIT_COST = 150
export const MENU_SCAN_MAX_FILES = 6
export const MENU_SCAN_MAX_PDFS = 1
export const MENU_SCAN_MAX_IMAGE_BYTES = 10 * 1024 * 1024
export const MENU_SCAN_MAX_PDF_BYTES = 15 * 1024 * 1024
export const MENU_MAX_DISHES_PER_DAY = 30
export const MENU_MAX_SECTIONS = 40
export const MENU_MAX_DISHES_PER_SECTION = 150
export const MENU_MAX_DISHES = 600

export const listingMenuKindSchema = z.enum(['lunch', 'full'])
export type ListingMenuKind = z.infer<typeof listingMenuKindSchema>

export const menuDietSchema = z.enum(['vegetarian', 'vegan'])
export type MenuDiet = z.infer<typeof menuDietSchema>

export const menuDishSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(300).nullable(),
  priceCents: z.number().int().min(0).max(100_000).nullable(),
  diets: z.array(menuDietSchema).max(2),
})
export type MenuDish = z.infer<typeof menuDishSchema>

/** ISO weekday: 1 = Monday … 7 = Sunday. */
export const isoWeekdaySchema = z.literal([1, 2, 3, 4, 5, 6, 7])
export type IsoWeekday = z.infer<typeof isoWeekdaySchema>

export const lunchMenuDaySchema = z.object({
  weekday: isoWeekdaySchema,
  dishes: z.array(menuDishSchema).min(1).max(MENU_MAX_DISHES_PER_DAY),
})
export type LunchMenuDay = z.infer<typeof lunchMenuDaySchema>

export const fullMenuSectionSchema = z.object({
  title: z.string().trim().min(1).max(80),
  dishes: z.array(menuDishSchema).min(1).max(MENU_MAX_DISHES_PER_SECTION),
})
export type FullMenuSection = z.infer<typeof fullMenuSectionSchema>

/** The Monday (YYYY-MM-DD) a one-week Mittagsmenü starts on. */
export const mondayDateSchema = z.iso.date().refine(isMonday, { message: 'Must be a Monday' })

const titleSchema = z.string().trim().min(1).max(80).nullable()
const noteSchema = z.string().trim().min(1).max(280).nullable()

const eachWeekdayOnce = (days: { weekday: number }[]) =>
  new Set(days.map((day) => day.weekday)).size === days.length
const withinDishLimit = (sections: { dishes: unknown[] }[]) =>
  sections.reduce((sum, section) => sum + section.dishes.length, 0) <= MENU_MAX_DISHES

export const lunchMenuInputSchema = z.object({
  title: titleSchema,
  note: noteSchema,
  /** Null when the board is the same every week. */
  weekOf: mondayDateSchema.nullable(),
  days: z
    .array(lunchMenuDaySchema)
    .min(1)
    .max(7)
    .refine(eachWeekdayOnce, { message: 'Each weekday only once' }),
})
export type LunchMenuInput = z.infer<typeof lunchMenuInputSchema>

export const fullMenuInputSchema = z.object({
  title: titleSchema,
  note: noteSchema,
  sections: z
    .array(fullMenuSectionSchema)
    .min(1)
    .max(MENU_MAX_SECTIONS)
    .refine(withinDishLimit, { message: `At most ${MENU_MAX_DISHES} dishes` }),
})
export type FullMenuInput = z.infer<typeof fullMenuInputSchema>

export const lunchMenuDtoSchema = lunchMenuInputSchema.extend({
  kind: z.literal('lunch'),
  updatedAt: z.string(),
})
export type LunchMenuDto = z.infer<typeof lunchMenuDtoSchema>

export const fullMenuDtoSchema = fullMenuInputSchema.extend({
  kind: z.literal('full'),
  updatedAt: z.string(),
})
export type FullMenuDto = z.infer<typeof fullMenuDtoSchema>

export const listingMenusDtoSchema = z.object({
  lunch: lunchMenuDtoSchema.nullable(),
  full: fullMenuDtoSchema.nullable(),
})
export type ListingMenusDto = z.infer<typeof listingMenusDtoSchema>

/** What a scan read: shaped like a saved menu, but it may hold nothing yet. */
export const scannedMenuSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('lunch'),
    title: titleSchema,
    note: noteSchema,
    weekOf: mondayDateSchema,
    days: z.array(lunchMenuDaySchema).max(7).refine(eachWeekdayOnce),
  }),
  z.object({
    kind: z.literal('full'),
    title: titleSchema,
    note: noteSchema,
    sections: z.array(fullMenuSectionSchema).max(MENU_MAX_SECTIONS).refine(withinDishLimit),
  }),
])
export type ScannedMenu = z.infer<typeof scannedMenuSchema>

export const menuScanWarningSchema = z.enum(['nothing_recognised'])
export type MenuScanWarning = z.infer<typeof menuScanWarningSchema>

/** One line of the scan's NDJSON stream. */
export const menuScanEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('progress'), dishes: z.number().int().min(0) }),
  z.object({
    type: z.literal('result'),
    menu: scannedMenuSchema,
    warnings: z.array(menuScanWarningSchema),
  }),
  z.object({ type: z.literal('error'), code: z.string() }),
])
export type MenuScanEvent = z.infer<typeof menuScanEventSchema>
